// MAGAZINE kit — the collection "shelf" (§1g) and the "box-set" tile used for
// groups in lists and as subgroup tiles inside a shelf. A shelf is a dense
// uniform cover grid (the bird's-eye view from the collections brief); a
// box-set tile is fanned mini-spines + name + count standing in for a whole
// collection.
import { el } from "../dom.js";
import { magCover, MAG } from "./kit.js";

// Per-category spine colours for the fanned-spine preview, in category order.
const SPINE = ["#8b3dff", "#ff7a1a", "#19d3e8", "#ff3d8b"];

// Fanned mini-spines preview (used by box-set tiles). `members` only drives how
// many spines we fan (max 3); colours rotate so it reads as a stack.
function fannedSpines(members, { height = "42px", width = "30px" } = {}) {
	const n = Math.min(3, Math.max(1, members.length || 1));
	const wrap = el("div", { style: { display: "flex", flex: "none" } });
	for (let i = 0; i < n; i++) {
		const c = SPINE[i % SPINE.length];
		wrap.append(el("div", {
			style: {
				width, height, borderRadius: "5px",
				background: `linear-gradient(150deg, ${c}, #0b0b0f)`,
				marginLeft: i === 0 ? "0" : "-12px", boxShadow: "0 2px 6px rgba(0,0,0,.4)",
			},
		}));
	}
	return wrap;
}

export { fannedSpines };

// A collection rendered as a dashed-border glass "box-set" tile: fanned spines +
// Anton name + "N inside ›". Used in the /groups grid and as a subgroup tile
// inside magShelf.
export function magBoxSetTile(group, members, { onClick, dashed = true } = {}) {
	return el("div", {
		role: "button",
		onclick: onClick,
		style: {
			display: "flex", flexDirection: "column", gap: "10px", justifyContent: "space-between",
			minHeight: "120px", padding: "13px", borderRadius: "14px", cursor: "pointer",
			background: MAG.bgGlass, backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)",
			border: dashed ? `1px dashed ${MAG.line}` : `1px solid ${MAG.line}`,
		},
	},
		fannedSpines(members, { height: "48px", width: "34px" }),
		el("div", {},
			el("div", {
				style: {
					fontFamily: "'Anton', sans-serif", fontSize: "18px", lineHeight: ".9",
					textTransform: "uppercase", color: "#fff",
					display: "-webkit-box", webkitLineClamp: "2", webkitBoxOrient: "vertical", overflow: "hidden",
				},
			}, group.name),
			el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9px",
					letterSpacing: ".06em", textTransform: "uppercase", color: MAG.lime, marginTop: "5px",
				},
			}, `${members.length} inside ›`),
		),
	);
}

// The shelf: a dense 3-column uniform cover grid of a collection's members.
// `ordered` adds a lime order badge per tile (01, 02, …) and assumes the
// caller passed members in order. `subgroups` (optional) render as box-set
// tiles ahead of the loose members. Tiles carry data-drag-id/-kind so the
// existing drag controller keeps working (drag-to-reorder / drag-to-group).
export function magShelf(members, {
	ordered = false,
	onTile,
	subgroups = [],
	onSubgroup,
} = {}) {
	const grid = el("div", {
		style: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" },
	});

	for (const sg of subgroups) {
		const tile = magBoxSetTile(sg.group, sg.members, { onClick: () => onSubgroup?.(sg.group) });
		grid.append(tile);
	}

	members.forEach((m, i) => {
		const children = [];
		if (ordered) {
			children.push(el("div", {
				style: {
					position: "absolute", top: "6px", left: "6px",
					fontFamily: "'Anton', sans-serif", fontSize: "15px", color: "#0b0b0f",
					background: MAG.lime, borderRadius: "6px", padding: "0 6px", lineHeight: "1.3",
					boxShadow: "0 2px 8px rgba(0,0,0,.35)",
				},
			}, String(i + 1).padStart(2, "0")));
		}
		children.push(el("div", {
			style: {
				position: "absolute", left: "7px", right: "7px", bottom: "7px",
				fontFamily: "'Anton', sans-serif", fontSize: "14px", lineHeight: ".88",
				textTransform: "uppercase", color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,.5)",
				display: "-webkit-box", webkitLineClamp: "2", webkitBoxOrient: "vertical", overflow: "hidden",
			},
		}, m.title ?? ""));

		const tile = magCover(m, {
			height: "150px", radius: "12px", scrim: true,
			onClick: () => onTile?.(m),
			children,
		});
		tile.dataset.dragId = m.id;
		tile.dataset.dragKind = "item";
		grid.append(tile);
	});

	return grid;
}
